import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { eventsI18n } from "@/utils/i18n";
import { publishEvent, restoreEvent, unpublishEvent } from "@/services/api/events";
import { useOwnedEvent } from "@/hooks/useOwnedEvent";
import { TrashedEventState } from "@/components/customer/events/TrashedEventState";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  Settings, 
  Gift, 
  Users, 
  Globe, 
  Calendar,
  CheckCircle,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { GeneralTab } from "./event/general/GeneralTab";
import { LandingTab } from "./event/LandingTab";
import { GiftsTab } from "./event/GiftsTab";
import { GuestsTab } from "./event/GuestsTab";
import { UserHeader } from "@/components/customer/UserHeader";
import { EventPlanUpgrade } from "@/components/customer/events/EventPlanUpgrade";
import { USER_ROUTES } from "@/routes";
import { getCurrentUser } from "@/services/api/auth";
import { forgetActiveEvent, rememberActiveEvent } from "@/utils/active-event";

export default function EventManagement() {
  const { t, currentLanguage } = useTranslation();
  const _locale = useRouteLocale();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentLocale = _locale || 'ro';
  const localeCode = currentLanguage as 'ro' | 'en';

  // JUB-157 F11: generation-guarded loading; a late response for another
  // route never replaces the event this route asks for.
  const { event, trashedEvent, loading, reload: loadEvent, refresh: refreshEvent, setEvent } = useOwnedEvent(id, (uuid) => {
    // JUB-156/157: never offer "back to this event" for one that cannot be opened.
    const userUuid = getCurrentUser()?.uuid;
    if (userUuid) forgetActiveEvent(userUuid, uuid);
  });
  const loadedRouteIdRef = useRef<string | undefined>(undefined);

  const location = useLocation();
  const editorTitle = event ? `${event.name} | Jubiel` : t("events.editor.metaTitle");
  usePageMeta({
    title: editorTitle,
    description: editorTitle,
    locale: currentLocale,
    path: location.pathname,
    noindex: true,
  });
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const [restoring, setRestoring] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Get active tab from URL, default to 'general'
  const tabFromUrl = searchParams.get('tab') || 'general';
  const activeTab = ['general', 'gifts', 'guests', 'landing'].includes(tabFromUrl) ? tabFromUrl : 'general';
  const loadedEventUuid = event?.uuid;

  useEffect(() => {
    tabsScrollRef.current
      ?.querySelector<HTMLElement>('[data-state="active"]')
      ?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [activeTab, loadedEventUuid]);

  useEffect(() => {
    if (loadedEventUuid && loadedEventUuid === id) {
      rememberActiveEvent(getCurrentUser()?.uuid, loadedEventUuid, activeTab);
    }
  }, [loadedEventUuid, id, activeTab]);

  useEffect(() => {
    // Check for payment success
    const paymentSuccess = searchParams.get('payment');
    const plan = searchParams.get('plan');
    if (paymentSuccess === 'success' && plan) {
      setShowPaymentSuccess(true);
      // Remove query params after showing message
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('payment');
      newSearchParams.delete('plan');
      setSearchParams(newSearchParams, { replace: true });
      
      // Auto-hide after 10 seconds
      setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 10000);
    }
    
    if (id) {
      // JUB-152: a query change on the same event (tab switch, payment
      // return) refreshes silently; only a new route shows the loading state,
      // which would unmount every tab and drop what the user is typing.
      if (loadedRouteIdRef.current === id) refreshEvent();
      else loadEvent();
      loadedRouteIdRef.current = id;
    } else {
      navigate(USER_ROUTES.events(currentLocale));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, searchParams]);

  const handleTabChange = (value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', value);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleRestoreTrashed = async () => {
    if (!trashedEvent) return;
    setRestoring(true);
    try {
      await restoreEvent(trashedEvent.uuid);
      toast.success(t('events.trash.restored'));
      await loadEvent();
    } catch {
      toast.error(t('events.trash.restoreError'));
    } finally {
      setRestoring(false);
    }
  };


  const handleTogglePublish = async () => {
    if (!event || publishing) return;

    try {
      setPublishing(true);
      const updatedEvent = event.live
        ? await unpublishEvent(event.uuid)
        : await publishEvent(event.uuid);
      setEvent(updatedEvent);
      toast.success(
        updatedEvent.live ? t('events.publishSuccess') : t('events.unpublishSuccess')
      );
    } catch (error: unknown) {
      console.error('Failed to toggle publish state:', error);
      toast.error(event.live ? t('events.unpublishError') : t('events.publishError'));
    } finally {
      setPublishing(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('events.dateTbd'); // JUB-154
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLocale === 'ro' ? 'ro-RO' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading || (event && event.uuid !== id)) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <UserHeader currentLocale={currentLocale} active="events" />
        <div className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!event && trashedEvent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center px-4">
        <TrashedEventState
          event={trashedEvent}
          currentLocale={currentLocale}
          restoring={restoring}
          onRestore={handleRestoreTrashed}
          onBack={() => navigate(USER_ROUTES.events(currentLocale))}
        />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10 flex items-center justify-center">
        <Card data-testid="editor-event-not-found">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('events.notFound') || eventsI18n.get('events.notFound', localeCode)}</p>
            <Button onClick={() => navigate(USER_ROUTES.events(currentLocale))} className="mt-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('common.back')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      <UserHeader currentLocale={currentLocale} active="events" />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)]">
        {/* Payment Success Banner */}
        {showPaymentSuccess && (
          <div className="mb-6 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 p-4 flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-900 dark:text-green-100">
                  {t('payment.welcomeTitle')}
                </h3>
                <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                  {t('payment.welcomeMessage', { 
                    plan: searchParams.get('plan') || 'standard'
                  })}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        <div className="mb-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="min-w-0 max-w-full break-words text-3xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                {event.name}
              </h1>
              <span
                data-testid="publish-status-chip"
                className={
                  event.live
                    ? "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300 border border-green-300 dark:border-green-800"
                    : "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-300 dark:border-amber-800"
                }
              >
                <span
                  className={
                    event.live
                      ? "h-1.5 w-1.5 rounded-full bg-green-500"
                      : "h-1.5 w-1.5 rounded-full bg-amber-500"
                  }
                />
                {event.live ? t('events.published') : t('events.notPublished')}
              </span>
            </div>
            <Button
              data-testid="publish-toggle-button"
              onClick={handleTogglePublish}
              disabled={publishing}
              variant={event.live ? "outline" : "default"}
              className={
                event.live
                  ? ""
                  : "bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
              }
            >
              {publishing
                ? t('common.loading')
                : event.live
                  ? t('events.form.unpublish')
                  : t('events.form.publish')}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(event.date)}</span>
            </div>
            {event.url && (
              <div className="flex items-center space-x-1">
                <Globe className="h-4 w-4" />
                <a 
                  href={`/@${event.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 hover:text-pink-700 dark:text-pink-400 dark:hover:text-pink-300"
                >
                  {t('events.viewPublic')}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="relative after:pointer-events-none after:absolute after:right-0 after:top-0 after:h-full after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent sm:after:hidden">
          <div
            ref={tabsScrollRef}
            data-testid="event-tabs-scroll"
            className="w-full border-b border-border overflow-x-auto overflow-y-hidden snap-x [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <TabsList className="inline-flex w-max h-auto items-center justify-start gap-1 bg-transparent p-0 border-0">
              <TabsTrigger 
                value="general" 
                className="inline-flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-none px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-0 border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-pink-500"
              >
                <Settings className="h-4 w-4" />
                <span>{t('events.form.generalInfo') || eventsI18n.get('events.form.generalInfo', localeCode)}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="gifts" 
                className="inline-flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-none px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-0 border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-pink-500"
              >
                <Gift className="h-4 w-4" />
                <span>{t('events.form.gifts')}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="guests" 
                className="inline-flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-none px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-0 border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-pink-500"
              >
                <Users className="h-4 w-4" />
                <span>{t('events.form.guests') || eventsI18n.get('events.form.guests', localeCode)}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="landing" 
                className="inline-flex shrink-0 snap-start items-center justify-center gap-2 whitespace-nowrap rounded-none px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-0 border-b-2 border-transparent data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-pink-500"
              >
                <Globe className="h-4 w-4" />
                <span>{t('events.form.landingPage')}</span>
              </TabsTrigger>
            </TabsList>
          </div>
          </div>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <EventPlanUpgrade
              eventUuid={event.uuid}
              eventName={event.name}
              currentLocale={currentLocale}
              currentPlan={event.plan}
              onPlanUpdated={loadEvent}
              confirmedGuests={event.stats?.confirmedGuests}
            />
            <GeneralTab
              event={event}
              onEventUpdated={refreshEvent}
              onEventPatched={setEvent}
            />
          </TabsContent>

          {/* Gifts Tab */}
          <TabsContent value="gifts" className="space-y-6">
            <GiftsTab event={event} locale={currentLocale} />
          </TabsContent>

          {/* Guests Tab */}
          <TabsContent value="guests" className="space-y-6">
            <GuestsTab event={event} onEventPatched={setEvent} />
          </TabsContent>

          {/* Landing Tab */}
          <TabsContent value="landing" className="space-y-6">
            <LandingTab event={event} locale={currentLocale} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
